"use client";

import { useEffect, useState } from "react";

type OverflowItem = {
  selector: string;
  left: number;
  right: number;
  width: number;
};

type ViewportSnapshot = {
  innerWidth: number;
  innerHeight: number;
  devicePixelRatio: number;
  clientWidth: number;
  clientHeight: number;
  scrollWidth: number;
  scrollHeight: number;
  visualWidth: number | null;
  visualHeight: number | null;
  orientation: string;
  safeArea: { top: number; right: number; bottom: number; left: number };
  overflowItems: OverflowItem[];
};

function rounded(value: number): number {
  return Math.round(value * 10) / 10;
}

function selectorFor(element: Element): string {
  const html = element as HTMLElement;
  if (html.id) return "#" + html.id;
  const classes = Array.from(html.classList).slice(0, 3);
  return html.tagName.toLowerCase() + (classes.length ? "." + classes.join(".") : "");
}

function isClippedInsideViewport(element: Element, clientWidth: number): boolean {
  let parent = element.parentElement;
  while (parent && parent !== document.body) {
    const style = getComputedStyle(parent);
    const clips = ["auto", "scroll", "hidden", "clip"].includes(style.overflowX);
    if (clips) {
      const bounds = parent.getBoundingClientRect();
      if (bounds.left >= -1 && bounds.right <= clientWidth + 1) return true;
    }
    parent = parent.parentElement;
  }
  return false;
}

function measureSafeArea(): ViewportSnapshot["safeArea"] {
  const probe = document.createElement("div");
  probe.setAttribute("aria-hidden", "true");
  probe.style.cssText = [
    "position:fixed",
    "visibility:hidden",
    "pointer-events:none",
    "padding-top:env(safe-area-inset-top)",
    "padding-right:env(safe-area-inset-right)",
    "padding-bottom:env(safe-area-inset-bottom)",
    "padding-left:env(safe-area-inset-left)",
  ].join(";");
  document.body.appendChild(probe);
  const style = getComputedStyle(probe);
  const safeArea = {
    top: Number.parseFloat(style.paddingTop) || 0,
    right: Number.parseFloat(style.paddingRight) || 0,
    bottom: Number.parseFloat(style.paddingBottom) || 0,
    left: Number.parseFloat(style.paddingLeft) || 0,
  };
  probe.remove();
  return safeArea;
}

function collectViewportSnapshot(): ViewportSnapshot {
  const root = document.documentElement;
  const clientWidth = root.clientWidth;
  const overflowItems = Array.from(document.querySelectorAll("body *"))
    .filter((element) => {
      const bounds = element.getBoundingClientRect();
      return (bounds.left < -1 || bounds.right > clientWidth + 1)
        && !isClippedInsideViewport(element, clientWidth);
    })
    .slice(0, 20)
    .map((element) => {
      const bounds = element.getBoundingClientRect();
      return {
        selector: selectorFor(element),
        left: rounded(bounds.left),
        right: rounded(bounds.right),
        width: rounded(bounds.width),
      };
    });
  const orientation = screen.orientation?.type
    ?? (window.matchMedia("(orientation: portrait)").matches ? "portrait" : "landscape");
  return {
    innerWidth: window.innerWidth,
    innerHeight: window.innerHeight,
    devicePixelRatio: window.devicePixelRatio,
    clientWidth,
    clientHeight: root.clientHeight,
    scrollWidth: root.scrollWidth,
    scrollHeight: root.scrollHeight,
    visualWidth: window.visualViewport ? rounded(window.visualViewport.width) : null,
    visualHeight: window.visualViewport ? rounded(window.visualViewport.height) : null,
    orientation,
    safeArea: measureSafeArea(),
    overflowItems,
  };
}

export function ViewportDiagnostics() {
  const [snapshot, setSnapshot] = useState<ViewportSnapshot | null>(null);

  useEffect(() => {
    let frame = 0;
    const update = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        const next = collectViewportSnapshot();
        setSnapshot(next);
        console.info("[P-013 viewport]", next);
        if (next.scrollWidth > next.clientWidth) console.table(next.overflowItems);
      });
    };
    const observer = new ResizeObserver(update);
    observer.observe(document.documentElement);
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    window.visualViewport?.addEventListener("resize", update);
    update();
    return () => {
      observer.disconnect();
      window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
      window.visualViewport?.removeEventListener("resize", update);
    };
  }, []);

  if (!snapshot) return null;
  const safe = snapshot.safeArea;
  return (
    <section className="viewport-diagnostics" aria-label="実機viewport診断">
      <div><span>VIEWPORT DIAGNOSTICS</span><strong>{snapshot.orientation}</strong><small>端末回転・表示領域変更時に自動更新</small></div>
      <code>inner {snapshot.innerWidth}×{snapshot.innerHeight} · client {snapshot.clientWidth}×{snapshot.clientHeight} · scroll {snapshot.scrollWidth}×{snapshot.scrollHeight} · visual {snapshot.visualWidth ?? "—"}×{snapshot.visualHeight ?? "—"} · DPR {snapshot.devicePixelRatio} · safe {safe.top}/{safe.right}/{safe.bottom}/{safe.left} · overflow {snapshot.overflowItems.length}</code>
      {snapshot.overflowItems.length > 0 && <small>{snapshot.overflowItems.map((item) => item.selector + " [" + item.left + "…" + item.right + "]").join(" · ")}</small>}
    </section>
  );
}